import { Injectable } from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';
import { FIELD_DETECTION_KEYWORDS } from '@config/field-detection-keywords.constant';
import type { FieldTypeKey } from '@config/field-detection-keywords.constant';

export interface IPdfFieldSuggestion {
  page: number;
  fieldType: FieldTypeKey;
  matchedText: string;
  matchedKeyword: string;
  confidence: number;
}

export interface IPdfInsights {
  numPages: number;
  previewText: string;
  entities: {
    emails: string[];
    phones: string[];
    dates: string[];
    amounts: string[];
  };
}

export interface IPdfStats {
  totalChars: number;
  totalWords: number;
  sizeBytes: number;
}

export interface IPdfPageText {
  page: number;
  text: string;
}

@Injectable({ providedIn: 'root' })
export class PdfFieldDetectionService {
  private _workerConfigured = false;

  private ensureWorker(): void {
    if (this._workerConfigured) return;
    try {
      const w = (pdfjsLib as any)?.GlobalWorkerOptions;
      // Keep consistent with other parts of app (pdf-view-edit).
      if (w && !w.workerSrc) w.workerSrc = '/assets/pdf.worker.min.mjs';
    } catch {
      // ignore
    } finally {
      this._workerConfigured = true;
    }
  }

 
  async scanPdfForSuggestions(
    base64: string,
    opts?: { maxPages?: number; maxSuggestions?: number },
  ): Promise<IPdfFieldSuggestion[]> {
    const suggestions: IPdfFieldSuggestion[] = [];
    const pdfBytes = this.base64ToBytes(base64);
    if (!pdfBytes) return suggestions;

    const maxSuggestions = Math.max(8, Math.min(opts?.maxSuggestions ?? 120, 500));

    try {
      this.ensureWorker();
      const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
      const pdf = await loadingTask.promise;
      const numPages = pdf.numPages;
      const maxPages = Math.max(1, Math.min(opts?.maxPages ?? numPages, numPages, 30));

      for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
        if (suggestions.length >= maxSuggestions) break;

        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const items = textContent.items.filter((item: any) => !item.type);

        let lineText = '';
        for (const item of items) {
          const str = (item as any).str || '';
          lineText += (item as any).hasEOL ? str + '\n' : str;
        }

        const lines = lineText.split(/\n/).map((s) => s.trim()).filter(Boolean);
        const pageNormalized = this.normalizeText(lineText);
        let pageMatchCount = 0;

        for (const rawLine of lines) {
          if (suggestions.length >= maxSuggestions || pageMatchCount >= 6) break;
          const text = this.normalizeText(rawLine);
          if (!text || text.length < 2) continue;
          const matchedRaw = rawLine.slice(0, 60);

          for (const category of FIELD_DETECTION_KEYWORDS) {
            if (suggestions.length >= maxSuggestions || pageMatchCount >= 6) break;
            for (const keyword of category.keywords) {
              const normalizedKeyword = this.normalizeText(keyword);
              if (!normalizedKeyword) continue;
              const m = this.matchKeyword(text, normalizedKeyword);
              if (m) {
                suggestions.push({
                  page: pageNum,
                  fieldType: category.fieldType,
                  matchedText: matchedRaw,
                  matchedKeyword: keyword,
                  confidence: m.score,
                });
                pageMatchCount++;
                break;
              }
            }
          }
        }

        for (const category of FIELD_DETECTION_KEYWORDS) {
          if (suggestions.length >= maxSuggestions) break;
          const hasForCategory = suggestions.some(
            (s) => s.page === pageNum && s.fieldType === category.fieldType,
          );
          if (hasForCategory) continue;
          for (const keyword of category.keywords) {
            const normalizedKeyword = this.normalizeText(keyword);
            if (!normalizedKeyword) continue;
            if (this.matchKeyword(pageNormalized, normalizedKeyword)) {
              suggestions.push({
                page: pageNum,
                fieldType: category.fieldType,
                matchedText: 'Found: "' + keyword + '"',
                matchedKeyword: keyword,
                confidence: 15,
              });
              pageMatchCount++;
              break;
            }
          }
        }
      }

      return this.deduplicateSuggestions(suggestions).slice(0, maxSuggestions);
    } catch {
      return [];
    }
  }

  async extractPdfInsights(base64: string, opts?: { maxPages?: number; maxChars?: number }): Promise<IPdfInsights | null> {
    const pdfBytes = this.base64ToBytes(base64);
    if (!pdfBytes) return null;

    const maxPages = Math.max(1, Math.min(opts?.maxPages ?? 2, 5));
    const maxChars = Math.max(200, Math.min(opts?.maxChars ?? 1600, 6000));

    try {
      this.ensureWorker();
      const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
      const pdf = await loadingTask.promise;
      const numPages = pdf.numPages;

      let combinedText = '';
      const pagesToRead = Math.min(numPages, maxPages);
      for (let pageNum = 1; pageNum <= pagesToRead; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const items = textContent.items.filter((item: any) => !item.type);
        for (const item of items) {
          const str = String((item as any).str ?? '');
          combinedText += (item as any).hasEOL ? str + '\n' : str + ' ';
          if (combinedText.length >= maxChars) break;
        }
        combinedText += '\n';
        if (combinedText.length >= maxChars) break;
      }

      const previewText = combinedText
        .replace(/\s+\n/g, '\n')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
        .slice(0, maxChars);

      const entities = this.extractEntities(previewText);
      return { numPages, previewText, entities };
    } catch {
      return null;
    }
  }

  /** Returns total character count (from all pages) and file size in bytes. */
  async getPdfStats(base64: string): Promise<IPdfStats | null> {
    const pdfBytes = this.base64ToBytes(base64);
    if (!pdfBytes) return null;

    try {
      this.ensureWorker();
      const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
      const pdf = await loadingTask.promise;
      const numPages = pdf.numPages;

      let fullText = '';
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const items = textContent.items.filter((item: any) => !item.type);
        for (const item of items) {
          fullText += String((item as any).str ?? '');
          if ((item as any).hasEOL) fullText += '\n';
        }
      }
      const totalChars = fullText.length;
      const totalWords = fullText.split(/\s+/).filter(Boolean).length;

      return { totalChars, totalWords, sizeBytes: pdfBytes.length };
    } catch {
      return null;
    }
  }

  /** Extract text from every page. Returns per-page text array. */
  async extractAllPagesText(base64: string): Promise<IPdfPageText[]> {
    const pdfBytes = this.base64ToBytes(base64);
    if (!pdfBytes) return [];

    try {
      this.ensureWorker();
      const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
      const pdf = await loadingTask.promise;
      const pages: IPdfPageText[] = [];

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const items = textContent.items.filter((item: any) => !item.type);
        let pageText = '';
        for (const item of items) {
          const str = String((item as any).str ?? '');
          pageText += (item as any).hasEOL ? str + '\n' : str + ' ';
        }
        pages.push({
          page: pageNum,
          text: pageText.replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim(),
        });
      }
      return pages;
    } catch {
      return [];
    }
  }

  /** Search a word/phrase across all pages. Returns pages + matched lines. */
  searchInPages(pages: IPdfPageText[], query: string): Array<{ page: number; lines: string[] }> {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    const results: Array<{ page: number; lines: string[] }> = [];
    for (const p of pages) {
      const lower = p.text.toLowerCase();
      if (!lower.includes(q)) continue;
      const matchedLines = p.text
        .split(/\n/)
        .filter(line => line.toLowerCase().includes(q))
        .slice(0, 3);
      results.push({ page: p.page, lines: matchedLines });
    }
    return results;
  }

  private normalizeText(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private base64ToBytes(input: string): Uint8Array | null {
    if (!input) return null;
    const raw = input.includes(',') ? input.split(',')[1] : input;
    if (!raw) return null;
    try {
      const binary = atob(raw);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
      return bytes;
    } catch {
      return null;
    }
  }

  private uniqCap(list: string[], cap: number): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const v of list) {
      const k = v.trim();
      if (!k) continue;
      const key = k.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(k);
      if (out.length >= cap) break;
    }
    return out;
  }

  private extractEntities(text: string): IPdfInsights['entities'] {
    const emails = this.uniqCap(
      (text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) ?? []).map((s) => s.trim()),
      5
    );

    const phones = this.uniqCap(
      (text.match(/(?:\+?\d[\d\s\-()]{7,}\d)/g) ?? [])
        .map((s) => s.replace(/\s+/g, ' ').trim())
        .filter((s) => /\d{7,}/.test(s.replace(/[^\d]/g, ''))),
      5
    );

    const dates = this.uniqCap(
      (text.match(/\b(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}|\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2})\b/g) ?? [])
        .map((s) => s.trim()),
      5
    );

    const amountsRaw: string[] = [
      ...(text.match(/(?:₹|\$|€|£)\s?\d[\d,]*(?:\.\d{1,2})?/g) ?? []),
      ...(text.match(/\b\d[\d,]*(?:\.\d{1,2})?\s?(?:INR|USD|EUR|GBP)\b/gi) ?? []),
    ];
    const amounts = this.uniqCap(
      amountsRaw.map((s) => s.replace(/\s+/g, ' ').trim()),
      5
    );

    return { emails, phones, dates, amounts };
  }

  private matchKeyword(text: string, keyword: string): { score: number } | null {
    if (!text || !keyword) return null;
    if (text === keyword) {
      return { score: 50 };
    }
    const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const boundary = new RegExp(`(?:^|\\s)${escaped}(?:\\s|$)`);
    if (boundary.test(text)) {
      return { score: 40 };
    }
    if (text.includes(keyword)) {
      return { score: 25 };
    }
    return null;
  }

  private deduplicateSuggestions(suggestions: IPdfFieldSuggestion[]): IPdfFieldSuggestion[] {
    const seen = new Set<string>();
    return suggestions.filter((s) => {
      const norm = s.matchedText.replace(/\s+/g, ' ').trim().slice(0, 40);
      const key = `${s.page}-${s.fieldType}-${norm}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }


  groupSuggestions(suggestions: IPdfFieldSuggestion[], maxSpots: number = 8): Array<{ page: number; matchedText: string; fieldTypes: FieldTypeKey[] }> {
    const map = new Map<string, { page: number; matchedText: string; fieldTypes: Set<FieldTypeKey> }>();
    for (const s of suggestions) {
      const norm = s.matchedText.replace(/\s+/g, ' ').trim().toLowerCase().slice(0, 35);
      const key = `${s.page}|${norm}`;
      const existing = map.get(key);
      if (existing) {
        existing.fieldTypes.add(s.fieldType);
        if (s.matchedText.length > (existing.matchedText?.length ?? 0)) {
          existing.matchedText = s.matchedText;
        }
      } else {
        map.set(key, {
          page: s.page,
          matchedText: s.matchedText,
          fieldTypes: new Set([s.fieldType]),
        });
      }
    }

    const groups = Array.from(map.values())
      .map((g) => ({ page: g.page, matchedText: g.matchedText, fieldTypes: Array.from(g.fieldTypes) }))
      .sort((a, b) => a.page - b.page);

    const maxPerPage = 2;
    const byPage = new Map<number, typeof groups>();
    for (const g of groups) {
      const list = byPage.get(g.page) ?? [];
      if (list.length < maxPerPage) list.push(g);
      byPage.set(g.page, list);
    }

    const result: Array<{ page: number; matchedText: string; fieldTypes: FieldTypeKey[] }> = [];
    const pages = Array.from(byPage.keys()).sort((a, b) => a - b);
    let round = 0;
    while (result.length < maxSpots && round < 3) {
      for (const p of pages) {
        const list = byPage.get(p) ?? [];
        if (list[round]) result.push(list[round]);
        if (result.length >= maxSpots) break;
      }
      round++;
    }
    return result.slice(0, maxSpots);
  }
}
